/**
 * Pyramid Import Modal
 * 
 * Multi-step modal for importing items into the Max Priorities Pyramid.
 * Supports import from Miro boards or Excel/CSV files.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
// Note: useMemo kept for integrationsApi
import {
  X,
  Upload,
  FileSpreadsheet,
  Download,
  ChevronRight,
  ChevronLeft,
  Check,
  AlertCircle,
  Loader2,
  Triangle,
  RefreshCw,
  ExternalLink,
} from 'lucide-react';
import {
  type PyramidImportResult,
  type PyramidItem,
  type TimeHorizon,
  getRowByTimeHorizon,
  createDefaultPyramidConfig,
  placeItemsInGrid,
} from '../../../types/prioritization/pyramid';
import {
  importFromExcel,
  downloadPyramidTemplate,
} from '../../../lib/pyramidImportService';
import { PyramidPreview } from './PyramidPreview';
import {
  getIntegrationsApi,
  type MiroBoard,
  type MiroTemplateImportResult,
} from '../../../lib/integrationsApi';

// ============================================================================
// Types
// ============================================================================

export interface PyramidImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with pyramid items - each item will be stored in pyramid + linked to a candidate */
  onImport: (
    pyramidItems: PyramidItem[], 
    duplicateHandling?: 'skip' | 'update' | 'create'
  ) => Promise<{ itemsCreated: number; itemsUpdated: number; itemsSkipped: number } | void>;
  /** Pre-selected Miro board ID if coming from Miro integration */
  miroPreselect?: string;
  /** Miro access token if already connected */
  miroToken?: string;
  /** Current categories/themes for the pyramid */
  categories?: string[];
  /** Number of existing items in the pyramid (for duplicate detection UI) */
  existingItemCount?: number;
}

type ImportStep = 'source' | 'upload' | 'miro-connect' | 'miro-board' | 'preview' | 'importing';
type ImportSource = 'excel' | 'miro';

// The pyramid template ID used for Miro import
const PYRAMID_TEMPLATE_ID = 'max-priorities-book-pyramid';

// ============================================================================
// Component
// ============================================================================

export function PyramidImportModal({
  isOpen,
  onClose,
  onImport,
  miroPreselect,
  miroToken,
  existingItemCount = 0,
}: PyramidImportModalProps) {
  // State
  const [step, setStep] = useState<ImportStep>(miroPreselect ? 'miro-board' : 'source');
  const [source, setSource] = useState<ImportSource>(miroPreselect ? 'miro' : 'excel');
  const [duplicateHandling, setDuplicateHandling] = useState<'skip' | 'update' | 'create'>('skip');
  const [file, setFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<PyramidImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Miro-specific state
  const [miroConnected, setMiroConnected] = useState(false);
  const [miroTokenInput, setMiroTokenInput] = useState(() => {
    // Try to get token from localStorage (stored by Connectors page)
    if (typeof window !== 'undefined') {
      return localStorage.getItem('peak_miro_token') || miroToken || '';
    }
    return miroToken || '';
  });
  const [miroBoards, setMiroBoards] = useState<MiroBoard[]>([]);
  const [selectedBoard, setSelectedBoard] = useState<MiroBoard | null>(null);
  const [miroPreviewData, setMiroPreviewData] = useState<MiroTemplateImportResult | null>(null);
  const [loadingBoards, setLoadingBoards] = useState(false);
  
  const integrationsApi = useMemo(() => getIntegrationsApi(), []);
  
  // Check if we have a stored Miro token
  const hasStoredMiroToken = typeof window !== 'undefined' && !!localStorage.getItem('peak_miro_token');
  
  // ========== Miro Connection Handlers ==========
  
  // Connect to Miro
  const handleMiroConnect = useCallback(async () => {
    if (!miroTokenInput.trim()) {
      setError('Please enter a Miro access token');
      return;
    }
    
    setIsProcessing(true);
    setError(null);
    
    try {
      const result = await integrationsApi.connectMiro(miroTokenInput.trim());
      
      if (result.connected) {
        setMiroConnected(true);
        setStep('miro-board');
        // Auto-load boards
        await loadMiroBoards();
      } else {
        setError(result.error || 'Failed to connect to Miro');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection failed');
    } finally {
      setIsProcessing(false);
    }
  }, [miroTokenInput, integrationsApi]);
  
  // Load Miro boards
  const loadMiroBoards = useCallback(async () => {
    setLoadingBoards(true);
    setError(null);
    
    try {
      const boards = await integrationsApi.listMiroBoards();
      setMiroBoards(boards);
      
      // If there's a preselected board, find and select it
      if (miroPreselect) {
        const preselected = boards.find(b => b.id === miroPreselect);
        if (preselected) {
          setSelectedBoard(preselected);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load boards');
    } finally {
      setLoadingBoards(false);
    }
  }, [integrationsApi, miroPreselect]);
  
  // Preview Miro import
  const handleMiroPreview = useCallback(async () => {
    if (!selectedBoard) {
      setError('Please select a board');
      return;
    }
    
    setIsProcessing(true);
    setError(null);
    
    try {
      // Import from Miro using template
      // Note: We use permissive options because:
      // 1. filterToTemplateBounds: false - board coordinates are normalized automatically
      // 2. includeUnzoned: true - items are placed in nearest zone if no exact match
      // 3. minConfidence: 'nearest' - accept items even without exact zone containment
      const result = await integrationsApi.importFromMiroTemplate(
        selectedBoard.id,
        PYRAMID_TEMPLATE_ID,
        {
          filterToTemplateBounds: false, // Coordinates are auto-normalized
          includeUnzoned: true, // Include all items, place in nearest zone
          minConfidence: 'nearest', // Accept nearest zone match
        }
      );
      
      console.log('[PyramidImportModal] Miro template result:', {
        itemCount: result.items?.length || 0,
        items: result.items?.map(i => ({ id: i.id, content: i.content?.substring(0, 30), zone: i.zone }))
      });
      
      setMiroPreviewData(result);
      
      // Transform Miro result to PyramidImportResult
      const pyramidResult = transformMiroResultToPyramid(result, selectedBoard.name);
      
      console.log('[PyramidImportModal] Transformed pyramid result:', {
        itemCount: pyramidResult.items?.length || 0,
        items: pyramidResult.items?.map(i => ({ id: i.id, title: i.title?.substring(0, 30), timeHorizon: i.timeHorizon }))
      });
      
      setImportResult(pyramidResult);
      setStep('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to preview board');
    } finally {
      setIsProcessing(false);
    }
  }, [selectedBoard, integrationsApi]);
  
  // Transform Miro import result to Pyramid format
  const transformMiroResultToPyramid = useCallback((
    miroResult: MiroTemplateImportResult,
    boardName: string
  ): PyramidImportResult => {
    // Extract unique themes from zone metadata
    const themeSet = new Set<string>();
    for (const item of miroResult.items) {
      const themeLabel = item.toolAttributes?.strategicThemeLabel as string;
      if (themeLabel) {
        themeSet.add(themeLabel);
      }
    }
    const themes = Array.from(themeSet);
    if (themes.length === 0) {
      themes.push('Process Automation', 'Tech Debt', 'Defects', 'Other Priorities');
    }
    
    // Create config
    const config = createDefaultPyramidConfig(boardName, 'miro', themes);
    
    // Transform items with Y-position based ordering within each zone
    // Group items by zone first
    const itemsByZone: Record<string, typeof miroResult.items> = {};
    for (const item of miroResult.items) {
      const zoneId = item.zone.id || 'unknown';
      if (!itemsByZone[zoneId]) {
        itemsByZone[zoneId] = [];
      }
      itemsByZone[zoneId].push(item);
    }
    
    // Sort items within each zone by Y position (top to bottom = highest to lowest priority)
    for (const zoneId of Object.keys(itemsByZone)) {
      itemsByZone[zoneId].sort((a, b) => {
        // Lower Y = higher on screen = higher priority (rank 1)
        const yDiff = a.position.y - b.position.y;
        if (yDiff !== 0) return yDiff;
        // Tiebreaker: X position (left to right)
        return a.position.x - b.position.x;
      });
    }
    
    // Convert to pyramid items with rankInCell
    const pyramidItems: PyramidItem[] = [];
    
    for (const [zoneId, zoneItems] of Object.entries(itemsByZone)) {
      zoneItems.forEach((item, index) => {
        const timeHorizon = (item.toolAttributes?.timeHorizon as TimeHorizon) || 'get_house_in_order';
        const strategicTheme = (item.toolAttributes?.strategicThemeLabel as string) || 'Other Priorities';
        const themeId = (item.toolAttributes?.strategicTheme as string) || 'other_priorities';
        
        // Find theme index
        const themeIndex = config.themes.findIndex(
          t => t.name.toLowerCase() === strategicTheme.toLowerCase() || t.id === themeId
        );
        const col = themeIndex >= 0 ? themeIndex : 0;
        
        pyramidItems.push({
          id: `miro-${item.id}`,
          title: item.content,
          description: undefined,
          timeHorizon,
          strategicThemeId: config.themes[col]?.id || themeId,
          strategicThemeName: strategicTheme,
          position: {
            row: getRowByTimeHorizon(timeHorizon),
            col,
          },
          source: 'miro',
          externalId: item.id,
          addedAt: new Date(),
          rankInCell: index + 1, // 1-based rank from Y-position sorting
          metadata: {
            miroPosition: item.position,
            zoneConfidence: item.zone.confidence,
            miroZoneId: zoneId,
          },
        });
      });
    }
    
    // Build grid
    const grid = placeItemsInGrid(config, pyramidItems);
    
    // Calculate summary
    const byTimeHorizon: Record<TimeHorizon, number> = {
      create_the_future: 0,
      reinforce_foundation: 0,
      get_house_in_order: 0,
    };
    const byTheme: Record<string, number> = {};
    
    for (const item of pyramidItems) {
      byTimeHorizon[item.timeHorizon]++;
      byTheme[item.strategicThemeName] = (byTheme[item.strategicThemeName] || 0) + 1;
    }
    
    return {
      success: true,
      config,
      items: pyramidItems,
      grid,
      summary: {
        totalItems: pyramidItems.length,
        byTimeHorizon,
        byTheme,
      },
      warnings: miroResult.warnings,
    };
  }, []);
  
  // Effect to check Miro connection on mount - use stored token from Connectors page
  useEffect(() => {
    if (isOpen) {
      const storedToken = localStorage.getItem('peak_miro_token');
      if (storedToken || miroToken) {
        setMiroTokenInput(storedToken || miroToken || '');
        // Auto-connect using stored token
        if (storedToken && !miroConnected) {
          integrationsApi.connectMiro(storedToken).then(result => {
            if (result.connected) {
              setMiroConnected(true);
            }
          });
        }
      }
    }
  }, [isOpen, miroToken, integrationsApi, miroConnected]);
  
  // ========== Excel Handlers ==========
  
  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    
    setError(null);
    setIsProcessing(true);
    setFile(selectedFile);
    
    try {
      const result = await importFromExcel(selectedFile);
      
      if (!result.success) {
        setError(result.error || 'Failed to parse file');
        setIsProcessing(false);
        return;
      }
      
      if (result.items.length === 0) {
        setError('No valid items found in the file');
        setIsProcessing(false);
        return;
      }
      
      setImportResult(result);
      setStep('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process file');
    } finally {
      setIsProcessing(false);
    }
  }, []);
  
  const handleDownloadTemplate = useCallback(() => {
    downloadPyramidTemplate();
  }, []);
  
  // ========== Navigation Handlers ==========
  
  const handleBack = useCallback(() => {
    switch (step) {
      case 'upload':
        setStep('source');
        setFile(null);
        setImportResult(null);
        break;
      case 'miro-connect':
        setStep('source');
        break;
      case 'miro-board':
        if (miroConnected && miroTokenInput) {
          // Stay connected, just go back to connect step to change token if needed
          setStep('source');
        } else {
          setStep('miro-connect');
        }
        setSelectedBoard(null);
        setMiroPreviewData(null);
        break;
      case 'preview':
        if (source === 'miro') {
          setStep('miro-board');
          setImportResult(null);
        } else {
          setStep('upload');
        }
        break;
    }
    setError(null);
  }, [step, source, miroConnected, miroTokenInput]);
  
  const [importStats, setImportStats] = useState<{ 
    itemsCreated: number; 
    itemsUpdated: number; 
    itemsSkipped: number 
  } | null>(null);
  
  const handleConfirmImport = useCallback(async () => {
    console.log('[PyramidImportModal] handleConfirmImport called');
    console.log('[PyramidImportModal] importResult:', {
      itemCount: importResult?.items?.length || 0,
      items: importResult?.items?.slice(0, 3).map(i => ({ id: i.id, title: i.title?.substring(0, 30) }))
    });
    console.log('[PyramidImportModal] duplicateHandling:', duplicateHandling);
    
    if (!importResult?.items || importResult.items.length === 0) {
      setError('No items to import');
      console.error('[PyramidImportModal] No items to import!');
      return;
    }
    
    setStep('importing');
    
    // Pass pyramid items with duplicate handling option
    try {
      console.log('[PyramidImportModal] Calling onImport with', importResult.items.length, 'items');
      const result = await onImport(importResult.items, duplicateHandling);
      
      if (result) {
        setImportStats(result);
        console.log('[PyramidImportModal] Import stats:', result);
      }
      
      // Short delay to show completion, then close
      setTimeout(() => {
        onClose();
        resetState();
      }, 1500);
    } catch (err) {
      console.error('[PyramidImportModal] Import failed:', err);
      setError(err instanceof Error ? err.message : 'Import failed');
      setStep('preview');
    }
  }, [importResult, onImport, onClose, duplicateHandling]);
  
  const resetState = useCallback(() => {
    setStep('source');
    setSource('excel');
    setFile(null);
    setImportResult(null);
    setError(null);
    setIsProcessing(false);
    // Don't reset Miro connection state to preserve token
    setSelectedBoard(null);
    setMiroPreviewData(null);
    setDuplicateHandling('skip');
    setImportStats(null);
  }, []);
  
  const handleClose = useCallback(() => {
    onClose();
    resetState();
  }, [onClose, resetState]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Triangle className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">
                Import Max Priorities Pyramid
              </h2>
              <p className="text-sm text-neutral-600">
                Import items organized by time horizon and strategic theme
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-neutral-400 hover:text-neutral-600 rounded-lg hover:bg-neutral-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Progress Steps */}
        <div className="px-6 py-3 border-b border-neutral-100 bg-neutral-50">
          <div className="flex items-center gap-2 text-sm">
            <StepIndicator 
              number={1} 
              label="Source" 
              isActive={step === 'source'} 
              isComplete={step !== 'source'} 
            />
            <ChevronRight className="h-4 w-4 text-neutral-300" />
            <StepIndicator 
              number={2} 
              label={source === 'miro' ? 'Select Board' : 'Upload'} 
              isActive={step === 'upload' || step === 'miro-connect' || step === 'miro-board'} 
              isComplete={['preview', 'importing'].includes(step)} 
            />
            <ChevronRight className="h-4 w-4 text-neutral-300" />
            <StepIndicator 
              number={3} 
              label="Preview & Import" 
              isActive={step === 'preview'} 
              isComplete={step === 'importing'} 
            />
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Error display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}
          
          {/* Step: Source Selection */}
          {step === 'source' && (
            <div className="space-y-4">
              <p className="text-sm text-neutral-600 mb-6">
                Choose how you want to import your pyramid data:
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Excel/CSV Option */}
                <button
                  onClick={() => {
                    setSource('excel');
                    setStep('upload');
                  }}
                  className="p-6 border-2 border-neutral-200 rounded-xl hover:border-indigo-400 hover:bg-indigo-50 transition-colors text-left group"
                >
                  <FileSpreadsheet className="h-8 w-8 text-neutral-400 group-hover:text-indigo-600 mb-3" />
                  <h3 className="font-medium text-neutral-900 mb-1">Excel / CSV File</h3>
                  <p className="text-sm text-neutral-500">
                    Upload a spreadsheet with items organized by time horizon and theme
                  </p>
                </button>
                
                {/* Miro Option */}
                <button
                  onClick={() => {
                    setSource('miro');
                    if (miroConnected) {
                      setStep('miro-board');
                      loadMiroBoards();
                    } else {
                      setStep('miro-connect');
                    }
                  }}
                  className="p-6 border-2 border-neutral-200 rounded-xl hover:border-yellow-400 hover:bg-yellow-50 transition-colors text-left group"
                >
                  <svg viewBox="0 0 24 24" className="w-8 h-8 text-neutral-400 group-hover:text-yellow-600 mb-3" fill="currentColor">
                    <path d="M17.392 0H13.9L17 4.808 10.592 0H7.1L12.1 7.385 3.392 0H0l9.592 11.196L0 24h3.392L12.1 12.017 7.1 24h3.492l6.408-14.4L13.9 24h3.492L24 10.588V0h-6.608z" />
                  </svg>
                  <h3 className="font-medium text-neutral-900 mb-1">Miro Board</h3>
                  <p className="text-sm text-neutral-500">
                    Import from a Miro board using the pyramid template
                  </p>
                  {miroConnected && (
                    <span className="mt-2 inline-flex items-center gap-1 text-xs text-green-600">
                      <Check className="h-3 w-3" />
                      Connected
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}
          
          {/* Step: Miro Connect */}
          {step === 'miro-connect' && (
            <div className="space-y-6">
              <div>
                <h3 className="font-medium text-neutral-900 mb-2">Connect to Miro</h3>
                <p className="text-sm text-neutral-600">
                  {hasStoredMiroToken 
                    ? 'You have a Miro connection from the Connectors settings. Click below to use it.'
                    : 'Enter your Miro access token to import from your boards, or connect via Settings → Connectors first.'}
                </p>
              </div>
              
              {hasStoredMiroToken ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700">
                      <Check className="h-5 w-5" />
                      <span className="font-medium">Miro connection available</span>
                    </div>
                    <p className="text-sm text-green-600 mt-1">
                      Using your existing connection from Settings → Connectors
                    </p>
                  </div>
                  
                  <button
                    onClick={handleMiroConnect}
                    disabled={isProcessing}
                    className="btn btn-primary disabled:opacity-50 w-full flex items-center justify-center gap-2"
                    style={{ backgroundColor: '#FFD02F', color: '#050038' }}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        Use Existing Connection
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-700">
                      <strong>Tip:</strong> For a persistent connection, go to{' '}
                      <a href="/prioritization/settings/connectors" className="underline font-medium">
                        Settings → Connectors
                      </a>{' '}
                      and connect your Miro account there.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Miro Access Token
                    </label>
                    <input
                      type="password"
                      value={miroTokenInput}
                      onChange={(e) => setMiroTokenInput(e.target.value)}
                      placeholder="Enter your Miro access token"
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      disabled={isProcessing}
                    />
                    <p className="mt-1 text-xs text-neutral-500">
                      You can get an access token from{' '}
                      <a 
                        href="https://miro.com/app/settings/user-profile/apps" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-yellow-600 hover:underline inline-flex items-center gap-1"
                      >
                        Miro Developer Settings
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </p>
                  </div>
                  
                  <button
                    onClick={handleMiroConnect}
                    disabled={isProcessing || !miroTokenInput.trim()}
                    className="btn btn-primary disabled:opacity-50 w-full flex items-center justify-center gap-2"
                    style={{ backgroundColor: '#FFD02F', color: '#050038' }}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        Connect to Miro
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Step: Miro Board Selection */}
          {step === 'miro-board' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-neutral-900 mb-1">Select a Miro Board</h3>
                  <p className="text-sm text-neutral-600">
                    Choose the board containing your Max Priorities Pyramid
                  </p>
                </div>
                <button
                  onClick={loadMiroBoards}
                  disabled={loadingBoards}
                  className="text-sm text-neutral-600 hover:text-neutral-900 flex items-center gap-1"
                >
                  <RefreshCw className={`h-4 w-4 ${loadingBoards ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
              
              {loadingBoards ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 text-yellow-500 animate-spin" />
                </div>
              ) : miroBoards.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-neutral-600">No boards found</p>
                  <p className="text-sm text-neutral-500 mt-1">
                    Make sure your token has access to boards
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {miroBoards.map((board) => (
                    <button
                      key={board.id}
                      onClick={() => setSelectedBoard(board)}
                      className={`w-full p-4 border rounded-lg text-left transition-colors ${
                        selectedBoard?.id === board.id
                          ? 'border-yellow-500 bg-yellow-50'
                          : 'border-neutral-200 hover:border-yellow-300 hover:bg-yellow-50/50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-neutral-900 truncate">
                            {board.name}
                          </h4>
                          {board.description && (
                            <p className="text-sm text-neutral-500 truncate mt-0.5">
                              {board.description}
                            </p>
                          )}
                          <p className="text-xs text-neutral-400 mt-1">
                            Modified: {new Date(board.modifiedAt).toLocaleDateString()}
                          </p>
                        </div>
                        {selectedBoard?.id === board.id && (
                          <Check className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              {selectedBoard && (
                <button
                  onClick={handleMiroPreview}
                  disabled={isProcessing}
                  className="btn btn-primary w-full flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#FFD02F', color: '#050038' }}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading Preview...
                    </>
                  ) : (
                    <>
                      Preview Import
                      <ChevronRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          )}
          
          {/* Step: Upload */}
          {step === 'upload' && source === 'excel' && (
            <div className="space-y-6">
              {/* Download Template */}
              <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Download className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-indigo-900 mb-1">
                      Need a template?
                    </h3>
                    <p className="text-sm text-indigo-700 mb-2">
                      Download our Excel template with the correct format for pyramid import.
                    </p>
                    <button
                      onClick={handleDownloadTemplate}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-800 underline"
                    >
                      Download Template (.xlsx)
                    </button>
                  </div>
                </div>
              </div>
              
              {/* File Upload */}
              <div className="flex flex-col items-center justify-center py-8">
                <label className="cursor-pointer">
                  <div className="flex flex-col items-center gap-4 p-8 border-2 border-dashed border-neutral-300 rounded-xl hover:border-indigo-400 hover:bg-indigo-50 transition-colors">
                    {isProcessing ? (
                      <Loader2 className="h-12 w-12 text-indigo-500 animate-spin" />
                    ) : (
                      <Upload className="h-12 w-12 text-neutral-400" />
                    )}
                    <div className="text-center">
                      <p className="text-lg font-medium text-neutral-900">
                        {isProcessing ? 'Processing...' : 'Drop file here or click to browse'}
                      </p>
                      <p className="text-sm text-neutral-500 mt-1">
                        Supports .xlsx, .xls, and .csv files
                      </p>
                    </div>
                  </div>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileChange}
                    className="hidden"
                    disabled={isProcessing}
                  />
                </label>
                {file && !isProcessing && (
                  <p className="mt-4 text-sm text-neutral-600">
                    Selected: {file.name}
                  </p>
                )}
              </div>
              
              {/* Format Requirements */}
              <div className="text-sm text-neutral-600">
                <h4 className="font-medium mb-2">Required columns:</h4>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Title</strong> - Name of the priority item</li>
                  <li><strong>Time Horizon</strong> - "Create the Future", "Reinforce Foundation", or "Get House in Order"</li>
                  <li><strong>Strategic Theme</strong> - Theme column (e.g., "Process Automation", "Tech Debt")</li>
                  <li><strong>Description</strong> (optional) - Additional details</li>
                  <li><strong>Order</strong> (optional) - Priority rank within each cell (1 = highest)</li>
                </ul>
              </div>
            </div>
          )}
          
          {/* Step: Preview */}
          {step === 'preview' && importResult && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-neutral-900">Preview Pyramid</h3>
                {importResult.warnings && importResult.warnings.length > 0 && (
                  <div className="text-sm text-amber-600 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {importResult.warnings.length} warning(s)
                  </div>
                )}
              </div>
              
              <PyramidPreview result={importResult} />
              
              {/* Duplicate Handling Option (only show if there are existing items) */}
              {existingItemCount > 0 && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <label className="block text-sm font-medium text-blue-800 mb-2">
                    If duplicates are found ({existingItemCount} existing items in pyramid)
                  </label>
                  <select
                    value={duplicateHandling}
                    onChange={(e) => setDuplicateHandling(e.target.value as 'skip' | 'update' | 'create')}
                    className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="skip">Skip duplicates (don't import if title already exists)</option>
                    <option value="update">Update existing (update position and details)</option>
                    <option value="create">Create anyway (may result in duplicates)</option>
                  </select>
                  <p className="mt-1 text-xs text-blue-600">
                    Duplicates are detected by matching item titles.
                  </p>
                </div>
              )}
              
              {/* Warnings */}
              {importResult.warnings && importResult.warnings.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="text-sm font-medium text-amber-800 mb-2">Warnings:</h4>
                  <ul className="text-sm text-amber-700 list-disc list-inside space-y-1">
                    {importResult.warnings.slice(0, 5).map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                    {importResult.warnings.length > 5 && (
                      <li>...and {importResult.warnings.length - 5} more</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          {/* Step: Importing */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-12">
              {importStats ? (
                <>
                  <Check className="h-12 w-12 text-green-500 mb-4" />
                  <p className="text-lg font-medium text-neutral-900">Import Complete!</p>
                  <div className="text-sm text-neutral-600 mt-2 space-y-1 text-center">
                    {importStats.itemsCreated > 0 && (
                      <p>Created {importStats.itemsCreated} new items</p>
                    )}
                    {importStats.itemsUpdated > 0 && (
                      <p>Updated {importStats.itemsUpdated} existing items</p>
                    )}
                    {importStats.itemsSkipped > 0 && (
                      <p>Skipped {importStats.itemsSkipped} duplicates</p>
                    )}
                    {importStats.itemsCreated === 0 && importStats.itemsUpdated === 0 && importStats.itemsSkipped > 0 && (
                      <p className="text-amber-600">All items already exist in the pyramid.</p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Loader2 className="h-12 w-12 text-indigo-500 animate-spin mb-4" />
                  <p className="text-lg font-medium text-neutral-900">Importing to Pyramid...</p>
                  <p className="text-sm text-neutral-500">
                    Processing {importResult?.items.length || 0} items
                  </p>
                </>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-between bg-neutral-50">
          <div className="text-sm text-neutral-600">
            {step === 'preview' && importResult && (
              <span>
                <strong>{importResult.items.length}</strong> items across{' '}
                <strong>{importResult.config.themes.length}</strong> themes will be imported as pyramid items and candidates
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {step !== 'source' && step !== 'importing' && (
              <button
                onClick={handleBack}
                className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-800 flex items-center gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </button>
            )}
            
            {step === 'preview' && (
              <button
                onClick={handleConfirmImport}
                disabled={!importResult?.items.length}
                className="btn btn-primary disabled:opacity-50"
              >
                <span className="flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  Import {importResult?.items.length || 0} Items
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Step Indicator Sub-component
// ============================================================================

function StepIndicator({ 
  number, 
  label, 
  isActive, 
  isComplete 
}: { 
  number: number; 
  label: string; 
  isActive: boolean; 
  isComplete: boolean;
}) {
  return (
    <div className={`flex items-center gap-2 ${isActive ? 'text-indigo-600' : isComplete ? 'text-green-600' : 'text-neutral-400'}`}>
      <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
        isActive ? 'bg-indigo-100 text-indigo-700' : 
        isComplete ? 'bg-green-100 text-green-700' : 
        'bg-neutral-200 text-neutral-500'
      }`}>
        {isComplete ? <Check className="h-3 w-3" /> : number}
      </div>
      <span className="font-medium">{label}</span>
    </div>
  );
}

export default PyramidImportModal;
